import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { ExpertLevel } from "@shopexperts/legacy-api";

interface UseAdminExpertLevelsOptions {
  isActive?: MaybeRef<boolean | undefined>;
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

export function useAdminExpertLevels(options: UseAdminExpertLevelsOptions = {}) {
  const api = useApi();
  const perPage = options.perPage ?? 50;
  const enabled = computed(() => unref(options.enabled) ?? true);
  const normalizedFilters = computed(() => ({
    is_active: unref(options.isActive),
    per_page: perPage,
  }));

  const query = useInfiniteQuery({
    queryKey: computed(() => ["admin", "expert-levels", normalizedFilters.value]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.expertLevels.listLevels({
        query: {
          ...normalizedFilters.value,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const levels = computed<ExpertLevel[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    levels,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
